import type { Container } from 'pixi.js';
import { getActiveDevice } from '@/input/active-device';
import { CARD_W, buildOfferCard } from './shop-cards';
import { buildRerollBar, buildShopHeader } from './shop-actions';
import { buildHangar, type HangarHooks } from './shop-hangar';
import type { HangarInteraction } from './shop-hangar-input';
import {
  CARDS_X, CARDS_Y, CHIPS_PER_ROW, CHIP_ROWS, COLLECTION_H, COLLECTION_W, COLLECTION_X,
  COLLECTION_Y, DESIGN_W, HANGAR_X, HANGAR_Y, PROMPT_Y, REROLL_Y, STATUS_H, STATUS_W, STATUS_X,
  chipPos,
} from './shop-layout';
import { buildPromptDock } from './shop-prompt';
import { buildCollection, buildNextSector } from './shop-right';
import { buildFocusTooltip, buildWeaponInfo } from './shop-tooltips';
import type { FocusItem, ShopState } from './shop-types';
import { buildStatsPanel } from './stats-panel';

/** Focus + click wiring every bay section shares. */
export interface BayHooks {
  isSel(item: FocusItem): boolean;
  interactive(node: Container, item: FocusItem): void;
}

export interface BayParts {
  base: BayHooks;
  hangar: HangarHooks;
  prompt: ReturnType<HangarInteraction['prompt']>;
  infoOpen: boolean;
  toggleLock(index: number): void;
  /** Header ‖ pause button (null on gamepad — Start opens the pause) */
  openMenu: (() => void) | null;
}

/**
 * Lay out one frame of the UPGRADE BAY (layout v4 + wave-4 hangar):
 * header vitals, offer cards, reroll, collection, ship status, sockets/bag,
 * next sector + CONTINUE, tooltips and the wordless footer prompt dock.
 */
export function composeBay(layer: Container, s: ShopState, sel: FocusItem | undefined, p: BayParts): void {
  const { base } = p;
  layer.addChild(buildShopHeader(s, DESIGN_W, base, p.openMenu));

  // ---- Offer cards (left column)
  const countOf = (id: string): number => s.collection.find((e) => e.card.id === id)?.count ?? 0;
  s.offer.forEach((oc, i) => {
    const item: FocusItem = { kind: 'card', index: i };
    const card = buildOfferCard(oc, s, base.isSel(item), countOf(oc.card.id) + 1, () => p.toggleLock(i));
    card.position.set(CARDS_X + i * (CARD_W + 14), CARDS_Y);
    base.interactive(card, item);
    layer.addChild(card);
  });

  // ---- Reroll bar — a single full-width strip atop the middle column
  const reroll = buildRerollBar(s, COLLECTION_W, base.isSel({ kind: 'reroll' }));
  reroll.position.set(COLLECTION_X, REROLL_Y);
  base.interactive(reroll, { kind: 'reroll' });
  layer.addChild(reroll);

  // ---- Card collection (middle column, below the reroll bar)
  layer.addChild(buildCollection(
    s, COLLECTION_X, COLLECTION_Y, COLLECTION_W, COLLECTION_H,
    CHIPS_PER_ROW * CHIP_ROWS, (i) => chipPos(i), base,
  ));

  // ---- Ship status panel (far right) with purchase / owned preview
  const focusedOffer =
    sel?.kind === 'card' && !s.offer[sel.index]?.bought ? s.offer[sel.index]!.card : null;
  const focusedOwned =
    sel?.kind === 'chip' ? s.collection.find((e) => e.card.id === sel.cardId)?.card ?? null : null;
  const statsPanel = buildStatsPanel(
    STATUS_W, STATUS_H, s.ctx, focusedOffer ?? focusedOwned, focusedOwned !== null,
  );
  statsPanel.position.set(STATUS_X, CARDS_Y);
  layer.addChild(statsPanel);

  // ---- Weapons: sockets 1-2-3 + compare strip, bag, swap-or-scrap crate
  const hangar = buildHangar(s, p.hangar);
  hangar.position.set(HANGAR_X, HANGAR_Y);
  layer.addChild(hangar);

  // ---- Next sector + CONTINUE (bottom right, B5 preview inside)
  layer.addChild(buildNextSector(s, STATUS_X, STATUS_W, base));

  const tip = buildFocusTooltip(s, sel, (i) => chipPos(i), {
    designW: DESIGN_W, maxChips: CHIPS_PER_ROW * CHIP_ROWS,
  });
  if (tip) layer.addChild(tip);

  // Weapon info popup (M1): floats above the hangar for the focused weapon
  const infoId = sel?.kind === 'weapon' ? sel.weaponId
    : sel?.kind === 'socket' ? s.equipped[sel.index] : undefined;
  if (p.infoOpen && infoId) {
    const info = buildWeaponInfo(infoId, s);
    info.position.set(380, HANGAR_Y - info.height - 8);
    layer.addChild(info);
  }

  // Footer dock: the focused item's one primary action, glyphs only
  const dock = buildPromptDock(getActiveDevice(), p.prompt.primary, {
    scrap: p.prompt.scrap, scrapValue: p.prompt.scrapValue, info: p.prompt.info,
    reroll: s.yellow >= s.rerollPrice,
  });
  dock.position.set(HANGAR_X, PROMPT_Y);
  layer.addChild(dock);
}
